import { server } from '../core/server/axios';
import { analyticsConfig, analyticsQuery } from './AnalyticsClient';

/**
 * Соблюдение лимитов и перемещения — отчёт сервиса ECASH Analytics: эпизоды дефицита, реакция на них,
 * базовый минимум, валютная позиция, работа с рекомендациями. Только чтение.
 *
 * Отчёт собирается ~40 с, поэтому на холодную бэк отвечает 503 («собирается»); клиент повторяет сам GET —
 * он либо отдаст данные, либо перезапустит сборку (опрос статуса зависал бы, если сборка упала).
 */

const COMPLIANCE = '/api/limits-compliance';

/** Бэк ещё собирает отчёт */
export const COMPLIANCE_BUILDING = Symbol('compliance-building');

export interface LcBucket {
    key: string;
    episodes: number;
    days: number;
    deficitKztDays: number;
    maxEpisodeDays: number;
    criticalDays: number;
    unconvertedDays: number;
}

export interface LcDay {
    key: string;
    cells: number;
    violations: number;
    sources: string[];
}

export interface LcViolations {
    available: boolean;
    reason?: string;
    episodes?: number;
    violationDays?: number;
    criticalDays?: number;
    deficitKztDays?: number;
    unconvertedDays?: number;
    cellsAffected?: number;
    longestEpisodeDays?: number;
    ongoingEpisodes?: number;
    byBranch?: LcBucket[];
    byCurrency?: LcBucket[];
    byDay?: LcDay[];
    daysWithoutSnapshot?: string[];
    sources?: string[];
}

export type LcReactionKind = "transfer" | "purchase" | "none";

export interface LcEpisode {
    branch: string;
    currency: string;
    start: string;
    end: string;
    days: number;
    criticalDays: number;
    warningDays: number;
    peakDeficitKzt: number | null;
    peakDeficitQty: number | null;
    deficitKztDays: number;
    unconvertedDays: number;
    sources: string[];
    endedByGap: boolean;
    ongoing: boolean;
    reactionKind: LcReactionKind;
    netBuyQty: number | null;
    transferDate?: string;
    reactionLatencyDays?: number;
    /** Первичные строки перемещений, закрывавших этот эпизод. */
    transfers?: { id: number; date: string; amount: number; from: string; by: string }[];
    movedQty?: number;
    /** null — пикового дефицита не знаем; «не знаем» ≠ «не покрыли». */
    covered?: boolean | null;
}

export interface LcReaction {
    withTransfer: number;
    withPurchase: number;
    withoutAction: number;
    stillOpen: number;
    openWithoutAction: number;
    medianLatencyDays: number | null;
    purchasesAvailable: boolean;
    graceDays: number;
    note?: string;
}

export interface LcUserRow {
    key: string;
    total: number;
    accepted: number;
    edited: number;
    rejected: number;
    routeChanged: number;
    amountChanged: number;
}

export interface LcBranchRow {
    key: string;
    total: number;
    accepted: number;
    edited: number;
    rejected: number;
}

export interface LcSuggestions {
    available: boolean;
    reason?: string;
    total?: number;
    byAction?: Record<string, number>;
    unchanged?: number;
    amountChangedOnly?: number;
    routeChangedOnly?: number;
    amountAndRouteChanged?: number;
    resolvedTransferMissing?: number;
    medianLatencyDays?: number | null;
    byUser?: LcUserRow[];
    byBranch?: LcBranchRow[];
    note?: string;
}

export interface LcFlowRow {
    key: string;
    count: number;
    volumeKzt: number;
    profitKzt: number;
    isCrossCity?: boolean;
}

export interface LcTransfers {
    available: boolean;
    reason?: string;
    count?: number;
    profitKzt?: number;
    volumeKzt?: number;
    noRateCount?: number;
    byCurrency?: LcFlowRow[];
    byRoute?: LcFlowRow[];
    byCity?: LcFlowRow[];
    franchise?: {
        count: number;
        profitKzt: number;
        volumeKzt: number;
        byRoute: { key: string; volumeKzt: number }[];
    };
    note?: string;
}

export interface LcRates {
    available: boolean;
    reason?: string;
    total?: number;
    underRule?: number;
    manualByDesign?: number;
    noRate?: number;
    beforeRuleInForce?: number;
    noRateRows?: { date: string | null; currency: string; route: string }[];
    ruleFrom?: string;
    note?: string;
}

export type LcVerdict = "ok" | "watch" | "bad" | "unknown";

export interface LcSummary {
    available: boolean;
    daysInPeriod: number;
    daysCovered?: number;
    episodes?: number;
    cellsAffected?: number;
    violationDays?: number;
    medianEpisodeDays?: number | null;
    longestEpisodeDays?: number;
    avgDailyDeficitKzt?: number | null;
    deficitKztDays?: number;
    unconvertedDays?: number;
    actedEpisodes?: number;
    actedSharePct?: number | null;
    medianLatencyDays?: number | null;
    stillOpen?: number;
    openWithoutAction?: number;
    purchasesAvailable?: boolean;
    topBranch?: LcBucket | null;
    topCurrency?: LcBucket | null;
    worstEpisode?: LcEpisode | null;
    verdict?: LcVerdict;
    verdictRule?: string;
}

export interface LcPrevious {
    from: string;
    to: string;
    days: number;
    available: boolean;
    /** false → сравнивать нечестно, причина в `reason`; цифры показывать нельзя. */
    comparable: boolean;
    reason?: string;
    episodes?: number;
    violationDays?: number;
    deficitKztDays?: number;
    longestEpisodeDays?: number;
    daysCovered?: number;
    sources?: string[];
}

export interface LcIgnoredRow {
    date: string;
    from: string;
    to: string;
    currency: string;
    amount: number;
    isPlanB?: boolean;
}

export interface LcMorning {
    available: boolean;
    reason?: string;
    /** Первый день, за который вообще есть утренний снимок. */
    startedOn?: string;
    total?: number;
    acted?: number;
    byAction?: Record<string, number>;
    resolvedItself?: number;
    ignored?: number;
    outcomeUnknown?: number;
    ignoredTop?: LcIgnoredRow[];
    daysCaptured?: number;
    daysWithoutCapture?: string[];
    captureTimes?: { date: string; at: string }[];
    note?: string;
}

export interface LcBaseLimitDay {
    date: string;
    targetUsd: number | null;
    factUsd: number | null;
    factEffectiveUsd: number | null;
    coveragePct: number | null;
    gapUsd: number | null;
    usdKzt: number | null;
    obligationsUsd: number | null;
    obligationsSource: string | null;
    source: string | null;
}

export interface LcBaseLimitBranch {
    key: string;
    days: number;
    gapUsdSum: number;
    lastBbUsd: number | null;
    lastFactUsd: number | null;
    lastWeight: number | null;
}

export interface LcBaseLimit {
    available: boolean;
    reason?: string;
    series?: LcBaseLimitDay[];
    byBranch?: LcBaseLimitBranch[];
    daysCovered?: number;
    daysInPeriod?: number;
    daysMissing?: string[];
    obligationsSources?: string[];
    note?: string;
}

export interface LcFxDay {
    date: string;
    assetsKzt: number;
    assetsUsd: number | null;
    hedgeFactUsd: number | null;
    liabilitiesUsd: number | null;
    netUsd: number | null;
    usdKzt: number | null;
    coveragePct: number | null;
    volumeEffectKzt: number | null;
    rateEffectKzt: number | null;
    noRateRows: number;
    rows: number;
    byCurrency: { currency: string; qty: number; kzt: number }[];
}

export interface LcFxPosition {
    available: boolean;
    reason?: string;
    currencies?: string[];
    series?: LcFxDay[];
    daysCovered?: number;
    daysInPeriod?: number;
    daysMissing?: string[];
    ratesAvailable?: boolean;
    liabilitiesError?: string | null;
    noRateRowsTotal?: number;
    note?: string;
}

export interface LcMorningBucket {
    key: string;
    cells: number;
    below: number;
    withResponse: number;
    noResponse: number;
    unknown: number;
}

export interface LcMorningWorst {
    date: string;
    branch: string;
    currency: string;
    workingMin: number;
    morningQty: number | null;
    deficitQty: number | null;
    morningSource: string | null;
    weakSource: boolean;
}

export interface LcMorningCompliance {
    available: boolean;
    reason?: string;
    cells?: number;
    ok?: number;
    belowWithResponse?: number;
    belowNoResponse?: number;
    unknown?: number;
    belowTotal?: number;
    respondedSharePct?: number | null;
    sources?: { open: number; prev_close: number };
    weakSourceCells?: number;
    purchasesAvailable?: boolean;
    byDay?: LcMorningBucket[];
    byBranch?: LcMorningBucket[];
    byCurrency?: LcMorningBucket[];
    worst?: LcMorningWorst[];
    note?: string;
}

export interface LcPayload {
    schemaVersion: number;
    from: string;
    to: string;
    summary: LcSummary;
    morning: LcMorning;
    morningCompliance: LcMorningCompliance;
    baseLimit: LcBaseLimit;
    fxPosition: LcFxPosition;
    previous: LcPrevious;
    violations: LcViolations;
    reaction: LcReaction;
    episodes: LcEpisode[];
    suggestions: LcSuggestions;
    transfers: LcTransfers;
    rates: LcRates;
}

type Opts = { signal?: AbortSignal };

export class LimitsComplianceController {
    /** Отчёт за период. Пока идёт сборка (503) возвращает COMPLIANCE_BUILDING, остальные ошибки — исключение */
    async getReport(dateFrom: string, dateTo: string, { signal }: Opts = {}): Promise<LcPayload | typeof COMPLIANCE_BUILDING> {
        try {
            return await server.get(`${COMPLIANCE}${analyticsQuery({ date_from: dateFrom, date_to: dateTo })}`, analyticsConfig({ signal }));
        } catch (e) {
            if ((e as { response?: { status?: number } })?.response?.status === 503) {
                return COMPLIANCE_BUILDING;
            }
            throw e;
        }
    }

    /** Принудительная пересборка отчёта за период */
    refresh(dateFrom: string, dateTo: string): Promise<unknown> {
        return server.post(`${COMPLIANCE}/refresh${analyticsQuery({ date_from: dateFrom, date_to: dateTo })}`, undefined, analyticsConfig());
    }
}
